/**
 * Cart Context
 *
 * Provides cart state management for donation items
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { DonationType } from '@/types/donation.types';
import type { ServiceType } from '@/types/service.types';

export interface CartItem {
  id: string;
  donationType?: DonationType;
  serviceType?: ServiceType;
  amount: number; // in cents
  description?: string;
  purposeId: number; // 1 for donation, 2 for service
}

interface CartContextValue {
  items: CartItem[];
  currentPurposeId: number | null;
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<Omit<CartItem, 'id'>>) => void;
  clearCart: () => void;
  clearCartAndSetPurpose: (purposeId: number) => void;
  getTotalAmount: () => number;
  getItemCount: () => number;
  scrollToCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [currentPurposeId, setCurrentPurposeId] = useState<number | null>(null);

  const addItem = useCallback((item: Omit<CartItem, 'id'>) => {
    setItems((prev) => {
      // Check if item with same donation/service type already exists
      const existingItemIndex = prev.findIndex((existingItem) => {
        // For donations: check if same donationType
        if (item.donationType && existingItem.donationType) {
          return existingItem.donationType.donationType === item.donationType.donationType;
        }
        // For services: check if same serviceType
        if (item.serviceType && existingItem.serviceType) {
          return existingItem.serviceType.donationType === item.serviceType.donationType;
        }
        return false;
      });

      // If item exists, increase its amount instead of adding duplicate
      if (existingItemIndex !== -1) {
        const updated = [...prev];
        const existingItem = updated[existingItemIndex];
        if (existingItem) {
          updated[existingItemIndex] = {
            id: existingItem.id,
            donationType: existingItem.donationType,
            serviceType: existingItem.serviceType,
            amount: existingItem.amount + item.amount,
            description: existingItem.description,
            purposeId: existingItem.purposeId,
          };
        }
        return updated;
      }

      // Otherwise, create new item
      const newItem: CartItem = {
        ...item,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
      return [...prev, newItem];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<Omit<CartItem, 'id'>>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const clearCartAndSetPurpose = useCallback(
    (purposeId: number) => {
      // Clear cart if switching between donation (1) and service (2)
      if (currentPurposeId !== null && currentPurposeId !== purposeId) {
        setItems([]);
      }
      setCurrentPurposeId(purposeId);
    },
    [currentPurposeId]
  );

  const getTotalAmount = useCallback(() => {
    return items.reduce((total, item) => total + item.amount, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.length;
  }, [items]);

  const scrollToCart = useCallback(() => {
    // Only scroll on mobile devices (less than lg breakpoint)
    if (window.innerWidth < 1024) {
      // Use setTimeout to allow state update and re-render before scrolling
      setTimeout(() => {
        const cartElement = document.getElementById('cart-section');
        if (cartElement) {
          cartElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const value: CartContextValue = {
    items,
    currentPurposeId,
    addItem,
    removeItem,
    updateItem,
    clearCart,
    clearCartAndSetPurpose,
    getTotalAmount,
    getItemCount,
    scrollToCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
